import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useIdentity } from '../contexts/IdentityContext';
import { useAuth } from '../contexts/AuthContext';
import { preachingApi } from '../lib/api';
import { ClipboardList, Plus, Heart, Trash2, Loader2, Filter, ImagePlus, X, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'presentoir', icon: '📋', color: 'bg-blue-500/10 text-blue-600' },
  { id: 'porte_a_porte', icon: '🚪', color: 'bg-green-500/10 text-green-600' },
  { id: 'rue', icon: '🚶', color: 'bg-amber-500/10 text-amber-600' },
  { id: 'telephone', icon: '📞', color: 'bg-purple-500/10 text-purple-600' },
  { id: 'courrier', icon: '✉️', color: 'bg-red-500/10 text-red-600' },
  { id: 'informel', icon: '💬', color: 'bg-teal-500/10 text-teal-600' },
];

// Fullscreen image viewer
const ImageViewer = ({ src, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center" onClick={onClose}>
    <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/20 rounded-full z-10">
      <X className="w-6 h-6 text-white" />
    </button>
    <img src={src} alt="" className="max-w-full max-h-full object-contain p-4" onClick={(e) => e.stopPropagation()} />
  </motion.div>
);

export default function PreachingPage() {
  const { t, language } = useLanguage();
  const { anonId } = useIdentity();
  const { isAuthenticated, user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [viewImage, setViewImage] = useState(null);

  // Create form
  const [newCategory, setNewCategory] = useState('porte_a_porte');
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);
  const [newNotes, setNewNotes] = useState('');
  const [newMedia, setNewMedia] = useState([]);
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await preachingApi.getReports(filter, 1, 50);
      setReports(res.data.reports || []);
    } catch {
      toast.error(t('error') || 'Error');
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const handleCreate = async () => {
    if (!newNotes.trim() && newMedia.length === 0) return;
    setCreating(true);
    try {
      const res = await preachingApi.createReport({
        category: newCategory,
        date: newDate,
        notes: newNotes.trim(),
        lang: language,
        media: newMedia.length > 0 ? newMedia : null,
      });
      setReports([res.data, ...reports]);
      setShowCreate(false);
      setNewNotes('');
      setNewMedia([]);
      toast.success(t('report_posted') || 'Report posted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error') || 'Error');
    } finally {
      setCreating(false);
    }
  };

  const handleLike = async (reportId) => {
    try {
      const res = await preachingApi.toggleLike(reportId, anonId);
      setReports(prev => prev.map(r => {
        if (r.id === reportId) {
          const liked = res.data.action === 'liked';
          return { ...r, likes_count: res.data.likes_count, likes: liked ? [...(r.likes||[]), anonId] : (r.likes||[]).filter(id => id !== anonId) };
        }
        return r;
      }));
    } catch { toast.error(t('error') || 'Error'); }
  };

  const handleDelete = async (reportId) => {
    try {
      await preachingApi.deleteReport(reportId);
      setReports(prev => prev.filter(r => r.id !== reportId));
      toast.success(t('report_deleted') || 'Deleted');
    } catch { toast.error(t('error') || 'Error'); }
  };

  const handleAddPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (newMedia.length >= 4 || file.size > 3000000) return;
      const reader = new FileReader();
      reader.onload = () => setNewMedia(prev => [...prev.slice(0, 3), { type: 'image', data: reader.result, caption: '' }]);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const getCat = (id) => CATEGORIES.find(c => c.id === id) || CATEGORIES[0];

  return (
    <div className="px-4 pt-4 pb-24">
      <AnimatePresence>{viewImage && <ImageViewer src={viewImage} onClose={() => setViewImage(null)} />}</AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-[var(--font-heading)] tracking-[-0.01em] text-foreground flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-primary" />
          {t('preaching') || 'Preaching'}
        </h1>
        <button onClick={() => {
            if (!isAuthenticated) { toast.info(t('login_to_post') || 'Please log in to post'); return; }
            setShowCreate(true);
          }}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
          data-testid="preaching-create-btn">
          <Plus className="w-3.5 h-3.5" />{t('share_preaching') || 'Share a report'}
        </button>
      </div>

      {/* Category Filter — 2 rows of 3, no horizontal scroll */}
      <div className="grid grid-cols-3 gap-1.5 mb-4">
        <button onClick={() => setFilter(null)}
          className={`px-2 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${!filter ? 'bg-primary text-primary-foreground' : 'bg-accent text-muted-foreground'}`}>
          {t('all') || 'All'}
        </button>
        {CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setFilter(filter === cat.id ? null : cat.id)}
            className={`px-2 py-1.5 rounded-lg text-[10px] font-medium transition-colors flex items-center justify-center gap-1 ${filter === cat.id ? 'bg-primary text-primary-foreground' : 'bg-accent text-muted-foreground'}`}>
            <span>{cat.icon}</span>{t(`cat_${cat.id}`) || cat.id}
          </button>
        ))}
      </div>

      {/* Create Dialog */}
      <AnimatePresence>
        {showCreate && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowCreate(false)} className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-50" />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-x-4 top-[5%] z-50 mx-auto max-w-md max-h-[90vh]">
              <div className="bg-card border border-border rounded-2xl p-5 shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-primary" />{t('new_report') || 'New Report'}
                  </h2>
                  <button onClick={() => setShowCreate(false)} className="p-1.5 rounded-lg hover:bg-accent"><X className="w-4 h-4" /></button>
                </div>

                {/* Category selector */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {CATEGORIES.map(cat => (
                    <button key={cat.id} onClick={() => setNewCategory(cat.id)}
                      className={`p-2 rounded-xl border text-xs font-medium text-center transition-colors ${newCategory === cat.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                      <span className="text-lg block mb-0.5">{cat.icon}</span>
                      {t(`cat_${cat.id}`) || cat.id}
                    </button>
                  ))}
                </div>

                {/* Date */}
                <div className="mb-3">
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Calendar className="w-3.5 h-3.5" />{t('date') || 'Date'}</label>
                  <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>

                {/* Notes */}
                <div className="mb-3">
                  <textarea value={newNotes} onChange={(e) => setNewNotes(e.target.value)}
                    placeholder={t('preaching_notes_placeholder') || 'Share your experience...'}
                    rows={3} className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary leading-6" />
                </div>

                {/* Media preview */}
                {newMedia.length > 0 && (
                  <div className="mb-3 grid grid-cols-2 gap-2">
                    {newMedia.map((m, i) => (
                      <div key={i} className="relative rounded-xl border overflow-hidden">
                        <img src={m.data} alt="" className="w-full h-24 object-cover" />
                        <button onClick={() => setNewMedia(prev => prev.filter((_, j) => j !== i))}
                          className="absolute top-1 right-1 p-0.5 bg-black/50 rounded-full"><X className="w-3 h-3 text-white" /></button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add photo + Submit */}
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleAddPhoto} className="hidden" />
                  <button onClick={() => fileRef.current?.click()} disabled={newMedia.length >= 4}
                    className="px-3 py-2.5 rounded-xl border border-border text-xs text-muted-foreground hover:bg-accent disabled:opacity-40">
                    <ImagePlus className="w-4 h-4" />
                  </button>
                  <button onClick={handleCreate} disabled={creating || (!newNotes.trim() && newMedia.length === 0)}
                    className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
                    {creating ? t('loading') : (t('post') || 'Post')}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Feed */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16">
          <ClipboardList className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{t('no_reports') || 'No reports yet'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map(report => {
            const cat = getCat(report.category);
            const liked = (report.likes || []).includes(anonId);
            const isOwner = report.author_id === user?.id;
            return (
              <motion.div key={report.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-[var(--radius)] border border-border bg-card shadow-sm p-4" data-testid={`preaching-card-${report.id}`}>
                {/* Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {report.author_photo ? <img src={report.author_photo} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-xs font-medium text-primary">{(report.author_name || 'A')[0].toUpperCase()}</span>}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{report.author_name || 'Anonymous'}</p>
                    <p className="text-[10px] text-muted-foreground">{report.date}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] font-medium ${cat.color}`}>
                    {cat.icon} {t(`cat_${report.category}`) || report.category}
                  </span>
                </div>

                {/* Notes */}
                {report.notes && <p className="text-sm leading-6 text-foreground/90 mb-3">{report.notes}</p>}

                {/* Media */}
                {report.media && report.media.length > 0 && (
                  <div className={`mb-3 ${report.media.length > 1 ? 'grid grid-cols-2 gap-2' : ''}`}>
                    {report.media.map((m, i) => (
                      <div key={i} className="rounded-xl overflow-hidden border border-border/50">
                        {m.type === 'image' && <img src={m.data} alt="" className="w-full max-h-48 object-cover cursor-pointer"
                          onClick={() => setViewImage(m.data)} />}
                        {m.caption && <p className="px-2 py-1 text-[10px] text-muted-foreground">{m.caption}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-4">
                  <button onClick={() => handleLike(report.id)}
                    className={`flex items-center gap-1.5 text-xs transition-colors ${liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'}`}>
                    <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />{report.likes_count || 0}
                  </button>
                  {isOwner && (
                    <button onClick={() => handleDelete(report.id)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
